import { useState, type FormEvent } from "react";
import {
  describeAuthError,
  registerWithEmail,
  resetPassword,
  signInWithEmail,
  signInWithGoogle,
} from "../lib/firebase";
import { analytics, AnalyticsEvent } from "../lib/analytics";
import { useTheme } from "../lib/theme";
import { BallMark } from "../App";
import { Button, ErrorNote, Icon, Input, SuccessNote, cx } from "../components/ui";

/**
 * Sign-in and registration. Credentials go straight to Firebase — this app never sees or
 * stores a password. The rules are on the page because most people arriving here have been
 * sent an invite code by a friend and want to know what they're joining first.
 */
export function AuthPage() {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Runs a Firebase auth action, tracking the attempt and outcome. `onSuccessEvent` is left
   *  undefined for actions — like a password reset — that aren't themselves a sign-in. */
  async function run(
    action: () => Promise<void>,
    method: "google" | "email",
    onSuccessEvent?: (typeof AnalyticsEvent)["SignInSuccess" | "SignUpSuccess"],
  ) {
    setError(null);
    setNotice(null);
    setBusy(true);
    analytics.track(AnalyticsEvent.SignInAttempt, { method, mode });
    try {
      await action();
      if (onSuccessEvent) analytics.track(onSuccessEvent, { method });
    } catch (err) {
      analytics.track(AnalyticsEvent.SignInFailure, { method, mode });
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void run(
      () =>
        mode === "login"
          ? signInWithEmail(email, password)
          : registerWithEmail(email, password, name),
      "email",
      mode === "login" ? AnalyticsEvent.SignInSuccess : AnalyticsEvent.SignUpSuccess,
    );
  }

  return (
    <div className="relative grid min-h-dvh lg:grid-cols-[1.05fr_0.95fr]">
      <ThemeCorner />
      <Pitch />

      <section className="relative flex items-center justify-center px-4 py-10 sm:px-8 sm:py-12">
        <div className="animate-in w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <div className="mb-5 flex items-center gap-2.5 text-lg font-bold text-fg">
              <BallMark size={30} />
              Last Man Standing
            </div>
            <h1 className="display-title text-[2.75rem] text-fg">Survive the weekend.</h1>
            <p className="mt-2.5 text-sm leading-relaxed text-fg-subtle">
              Pick one team a week. Never the same team twice. Outlast everyone.
            </p>
          </div>

          <div className="card p-5 sm:p-7 lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none">
            <div className="mb-6 hidden lg:block">
              <h2 className="display-title text-3xl text-fg">
                {mode === "login" ? "Welcome back." : "Create your account."}
              </h2>
              <p className="mt-2 text-sm text-fg-subtle">
                {mode === "login"
                  ? "Sign in to make this week's pick."
                  : "It takes about ten seconds."}
              </p>
            </div>

            <Button
              variant="secondary"
              size="lg"
              block
              loading={busy}
              onClick={() => void run(signInWithGoogle, "google", AnalyticsEvent.SignInSuccess)}
            >
              <GoogleMark />
              Continue with Google
            </Button>

            <div className="my-6 flex items-center gap-3">
              <span className="h-px flex-1 bg-line" />
              <span className="text-[12px] font-medium uppercase tracking-wider text-fg-faint">
                or with email
              </span>
              <span className="h-px flex-1 bg-line" />
            </div>

            <div className="mb-5 grid grid-cols-2 rounded-2xl border border-line bg-surface-sunken p-1">
              {(["login", "register"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={mode === value}
                  onClick={() => {
                    setMode(value);
                    setError(null);
                    setNotice(null);
                  }}
                  className={cx(
                    "min-h-10 whitespace-nowrap rounded-xl px-3 py-2 text-[13px] font-semibold transition-all duration-200 sm:text-sm",
                    mode === value
                      ? "bg-accent text-accent-fg shadow-sm"
                      : "text-fg-subtle hover:text-fg",
                  )}
                >
                  {value === "login" ? "Sign in" : "Create account"}
                </button>
              ))}
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              {mode === "register" && (
                <Input
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Lewis"
                  autoComplete="name"
                  icon="user"
                  hint="You'll pick the username other players see next."
                />
              )}

              <Input
                label="Email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />

              <Input
                label="Password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                {...(mode === "register" ? { hint: "At least 6 characters." } : {})}
              />

              {error && <ErrorNote>{error}</ErrorNote>}
              {notice && <SuccessNote>{notice}</SuccessNote>}

              <Button type="submit" size="lg" loading={busy} block>
                {mode === "login" ? "Sign in" : "Create account"}
                <Icon name="arrowRight" className="size-4" />
              </Button>
            </form>

            {mode === "login" && (
              <button
                type="button"
                onClick={() =>
                  void run(async () => {
                    if (!email) {
                      throw new Error("Enter your email address first, then tap this again.");
                    }
                    await resetPassword(email);
                    analytics.track(AnalyticsEvent.PasswordResetRequested);
                    setNotice("Check your inbox for a reset link.");
                  }, "email")
                }
                className="mt-4 w-full text-center text-[13px] text-fg-subtle transition-colors hover:text-fg"
              >
                Forgot your password?
              </button>
            )}

            <p className="mt-6 text-center text-[12px] leading-relaxed text-fg-faint">
              Sign-in is handled by Firebase — we never see your password.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

/** The pitch: what the game actually is, for someone who arrived from a mate's invite code. */
function Pitch() {
  return (
    <section className="relative hidden flex-col justify-between overflow-hidden border-r border-line p-12 xl:p-16 lg:flex">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 72% 58% at 18% 18%, var(--t-accent-soft), transparent 62%)," +
            "radial-gradient(ellipse 60% 50% at 82% 84%, var(--t-info-soft), transparent 64%)",
        }}
      />

      <div className="flex items-center gap-3 text-lg font-bold tracking-tight text-fg">
        <BallMark size={32} />
        Last Man Standing
      </div>

      <div className="max-w-xl">
        <p className="label mb-4 text-accent-text">The weekly football survivor</p>
        <h1 className="display-title text-7xl text-fg xl:text-[5.5rem]">
          One team a week.
          <br />
          <span className="text-accent-text">Never the same team twice.</span>
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-fg-subtle">
          Pick a team you think will win. If they win, you're through. Draw or lose and you're
          out. The catch: once you've used a team, they're gone for the rest of the season — so
          spending Arsenal in week one might cost you in March.
        </p>

        <ul className="mt-10 space-y-4">
          {(
            [
              ["skull", "Sudden death", "One non-win and your season is over."],
              ["shield", "Hidden picks", "Nobody sees your team until the deadline passes."],
              ["settings", "Your rules", "Whoever starts the competition sets lives, draws and deadlines."],
            ] as const
          ).map(([icon, title, body]) => (
            <li key={title} className="flex gap-3.5">
              <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-xl border border-accent-line bg-accent-soft text-accent-text">
                <Icon name={icon} className="size-[18px]" />
              </span>
              <span className="text-sm leading-relaxed text-fg-subtle">
                <strong className="block font-semibold text-fg">{title}</strong>
                {body}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-sm text-fg-faint">No payments. No prizes handled here. Just bragging rights.</p>
    </section>
  );
}

/** The one control available before signing in — someone shouldn't have to log in to stop the
 *  page burning their eyes at midnight. */
function ThemeCorner() {
  const { resolved, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={`Switch to ${resolved === "dark" ? "light" : "dark"} mode`}
      className="absolute right-4 top-4 z-20 grid size-10 place-items-center rounded-xl border border-line bg-surface/80 text-fg-subtle backdrop-blur transition-colors hover:text-fg sm:right-6 sm:top-6"
    >
      <Icon name={resolved === "dark" ? "moon" : "sun"} className="size-[18px]" />
    </button>
  );
}

function GoogleMark() {
  return (
    <svg className="size-[18px]" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M45.1 24.5c0-1.6-.1-3.2-.4-4.7H24v8.9h11.8c-.5 2.8-2 5.1-4.3 6.7v5.5h7c4.1-3.8 6.6-9.4 6.6-16.4z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.8 0 10.7-1.9 14.3-5.2l-7-5.5c-1.9 1.3-4.4 2.1-7.3 2.1-5.6 0-10.4-3.8-12.1-8.9H4.7v5.6C8.3 41.4 15.6 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.9 28.5c-.4-1.3-.7-2.7-.7-4.1s.2-2.8.7-4.1v-5.6H4.7C3.2 17.6 2.4 20.7 2.4 24s.8 6.4 2.3 9.2l7.2-4.7z"
      />
      <path
        fill="#EA4335"
        d="M24 11.4c3.2 0 6 1.1 8.2 3.2l6.2-6.2C34.7 4.9 29.8 3 24 3 15.6 3 8.3 7.6 4.7 14.7l7.2 5.6c1.7-5.1 6.5-8.9 12.1-8.9z"
      />
    </svg>
  );
}
